/**
 * `window.walkPlayer`: read-only hooks on the Player page for headless checks (walk-bake#11 C15,
 * C20). The viewer is an iframe of the same origin, so these read its `window.walkbake`; with no
 * walk open (or a viewer not ready yet) they return null.
 */

export interface TextureFormat {
  /** "astc-hdr-4x4" | "rgb9e5" | "half-float" */
  lightmaps: string;
  /** "astc-ldr-6x6" | "orig" */
  textures: string;
  /** The estimate for what loads (what the loading screen shows), or null without one. */
  gpu_mb: number | null;
}

interface ViewerApi {
  textureFormat?: () => (TextureFormat & { line?: string }) | null;
  stats?: () => { fps?: number };
}

function viewerApi(win: Window | null | undefined): ViewerApi | null {
  try {
    return (win as unknown as { walkbake?: ViewerApi } | null | undefined)?.walkbake ?? null;
  } catch {
    return null; // cross-origin or gone
  }
}

/** { lightmaps, textures, gpu_mb } of the open walk, or null (no walk, or the choice is not made yet). */
export function textureFormatOf(win: Window | null | undefined): TextureFormat | null {
  try {
    const format = viewerApi(win)?.textureFormat?.();
    return format ? { lightmaps: format.lightmaps, textures: format.textures, gpu_mb: format.gpu_mb } : null;
  } catch {
    return null;
  }
}

/** The viewer's current frames per second (the average the debug panel shows), or null. */
export function fpsOf(win: Window | null | undefined): number | null {
  try {
    const fps = viewerApi(win)?.stats?.().fps;
    return typeof fps === "number" && Number.isFinite(fps) ? fps : null;
  } catch {
    return null;
  }
}

export interface WalkPlayerApi {
  textureFormat(): TextureFormat | null;
  fps(): number | null;
}

/** viewerWindow: the open walk's window, or null when no walk is open. */
export function createWalkPlayerApi(viewerWindow: () => Window | null): WalkPlayerApi {
  return {
    textureFormat: () => textureFormatOf(viewerWindow()),
    fps: () => fpsOf(viewerWindow()),
  };
}
